<script>
  import { onDestroy, onMount, tick } from 'svelte';
  import { readStageSize, shouldRepaintSize, applyCanvasBitmap } from './atlasLayout.js';
  import { surfaceGroups, groupForRoom } from './surfaceAtlas.js';
  import { paintAtlas, isCurrentPlace, panToCenterPlace, onMapTilesReady, clampMapScale, setYouPortrait } from './atlasRenderer.js';

  export let store = null;
  export let sendMessage = null;

  let atlas = emptyAtlas();
  let currentRoomId = null;
  let activeLayer = '';

  let travelTargetId = null;
  let travelPath = [];
  let isTraveling = false;
  let travelPathRoomIds = new Set();
  let panAnimRaf = 0;

  let panX = 0;
  let panY = 0;
  let userScale = 1;
  let isPanning = false;
  let didDrag = false;
  let panStart = { x: 0, y: 0, panX: 0, panY: 0 };
  let tooltip = { visible: false, text: '', x: 0, y: 0 };

  let widgetWrap, widgetCanvas;
  let widgetObserver;
  let stopTileListener;
  const hitState = { items: [] };
  let lastWidgetSize = null;
  let selectedId = null;
  let drawRaf = 0;

  // Badge / chrome only — fullscreen lives in MapOverviewOverlay (body portal).
  $: mapOpen = !!(store && $store && $store.mapOverviewOpen);

  function emptyAtlas() {
    return { characterId: '', currentRoomId: '', currentLayer: '', layers: [], places: [], paths: [], regions: [] };
  }

  function resolveLayer(data, roomId, preferred) {
    const places = data.places || [];
    if (preferred && places.some(p => p.layer === preferred)) return preferred;
    const here = places.find(p => p.id === roomId) || places.find(p => isCurrentPlace(p.id, roomId));
    if (here && here.layer) return here.layer;
    if (data.currentLayer && places.some(p => p.layer === data.currentLayer)) return data.currentLayer;
    if (data.layers && data.layers[0]) return data.layers[0].id;
    const first = places[0];
    return (first && first.layer) || 'overworld';
  }

  $: if (store) {
    const nextAtlas = $store.atlas && Array.isArray($store.atlas.places) ? $store.atlas : emptyAtlas();
    const newRoomId = $store.currentRoomId || nextAtlas.currentRoomId || null;
    const roomChanged = newRoomId !== currentRoomId;
    const atlasChanged = nextAtlas !== atlas;
    if (atlasChanged) atlas = nextAtlas;
    if (roomChanged) {
      currentRoomId = newRoomId;
      if (isTraveling && newRoomId) advanceTravel(newRoomId);
    }
    const nextLayer = resolveLayer(atlas, currentRoomId, $store.atlasLayer || activeLayer);
    const layerChanged = nextLayer !== activeLayer;
    if (layerChanged) activeLayer = nextLayer;
    if (roomChanged || layerChanged || (atlasChanged && currentRoomId)) {
      tick().then(() => {
        if (isTraveling) animatePanToYou(220);
        else {
          applyRecenterToYou(true);
          scheduleDraw();
        }
      });
    } else if (atlasChanged) {
      scheduleDraw();
    }
  }

  $: if (store && $store.character) setYouPortrait($store.character.portrait || '');

  $: visiblePlaces = (atlas.places || []).filter(p => p.layer === activeLayer);
  $: visibleRegions = (atlas.regions || []).filter(r => r.layer === activeLayer);
  $: if (store && $store.mapSelectedId) selectedId = $store.mapSelectedId;
  $: selectedPlace = (atlas.places || []).find(p => p.id === selectedId) || null;
  $: canTravel = !!(selectedPlace && selectedPlace.discovered && selectedPlace.id !== currentRoomId && !isCurrentPlace(selectedPlace.id, currentRoomId));

  function requestTravel() {
    if (!canTravel || isTraveling || !selectedPlace) return;
    startTravel(selectedPlace.id);
  }

  function findPath(startId, targetId) {
    if (!startId || !targetId || startId === targetId) return null;
    const byId = {};
    for (const p of atlas.places || []) byId[p.id] = p;
    if (!byId[startId] || !byId[targetId] || !byId[startId].discovered || !byId[targetId].discovered) {
      return null;
    }
    const adj = {};
    for (const path of atlas.paths || []) {
      if (!byId[path.from] || !byId[path.to] || !byId[path.from].discovered) continue;
      if (!adj[path.from]) adj[path.from] = [];
      adj[path.from].push({ to: path.to, dir: path.dir });
    }
    const queue = [startId];
    const seen = new Set([startId]);
    const parent = {};
    while (queue.length) {
      const id = queue.shift();
      for (const edge of adj[id] || []) {
        if (seen.has(edge.to)) continue;
        if (!byId[edge.to] || (!byId[edge.to].discovered && edge.to !== targetId)) continue;
        seen.add(edge.to);
        parent[edge.to] = { parentId: id, direction: edge.dir };
        queue.push(edge.to);
        if (edge.to === targetId) {
          const steps = [];
          let cur = targetId;
          while (parent[cur]) {
            steps.unshift({ roomId: cur, direction: parent[cur].direction });
            cur = parent[cur].parentId;
          }
          return steps;
        }
      }
    }
    return null;
  }

  function stopPanAnim() {
    if (panAnimRaf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(panAnimRaf);
    panAnimRaf = 0;
  }

  function animatePanToYou(durationMs = 220) {
    const size = readStageSize(widgetWrap);
    const here = resolveHerePlace();
    let target = { panX: 0, panY: 0 };
    if (here && size.w >= 4 && size.h >= 4) {
      target = panToCenterPlace(surfaceGroups(visiblePlaces, activeLayer), here, size.w, size.h, userScale, atlas.paths || []);
    }
    stopPanAnim();
    const fromX = panX, fromY = panY;
    const dx = target.panX - fromX, dy = target.panY - fromY;
    if (!durationMs || (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5)) {
      panX = target.panX; panY = target.panY; scheduleDraw(); return;
    }
    const start = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      const e = ease(t);
      panX = fromX + dx * e;
      panY = fromY + dy * e;
      scheduleDraw();
      if (t < 1) panAnimRaf = requestAnimationFrame(step);
      else { panAnimRaf = 0; panX = target.panX; panY = target.panY; scheduleDraw(); }
    };
    panAnimRaf = requestAnimationFrame(step);
  }

  function startTravel(targetId) {
    if (!currentRoomId || targetId === currentRoomId) return;
    const path = findPath(currentRoomId, targetId);
    if (!path || !path.length) return;
    travelTargetId = targetId;
    travelPath = path;
    isTraveling = true;
    travelPathRoomIds = new Set([currentRoomId, ...path.map(s => s.roomId)]);
    animatePanToYou(280);
    if (sendMessage) sendMessage(path[0].direction);
  }

  function cancelTravel() {
    travelTargetId = null;
    travelPath = [];
    isTraveling = false;
    travelPathRoomIds = new Set();
  }

  function advanceTravel(newRoomId) {
    if (!isTraveling || !travelPath.length) {
      cancelTravel();
      return;
    }
    if (travelPath[0].roomId === newRoomId) {
      travelPath = travelPath.slice(1);
      travelPathRoomIds = new Set(travelPath.map(s => s.roomId));
      if (!travelPath.length) cancelTravel();
      else setTimeout(() => { if (sendMessage && travelPath[0]) sendMessage(travelPath[0].direction); }, 160);
    } else {
      cancelTravel();
    }
  }

  function paint(canvas, wrap) {
    if (!canvas || !wrap) return;
    const size = readStageSize(wrap);
    if (size.w < 4 || size.h < 4) return;
    const dpr = window.devicePixelRatio || 1;
    applyCanvasBitmap(canvas, size.w, size.h, dpr);
    const w = size.w;
    const h = size.h;

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const result = paintAtlas(ctx, {
      w,
      h,
      atlas,
      activeLayer,
      visiblePlaces,
      visibleRegions,
      currentRoomId,
      maximized: false,
      panX,
      panY,
      userScale,
      travelPathRoomIds,
      travelTargetId,
      selectedId,
    });
    hitState.items = result.hits;
  }

  function scheduleDraw() {
    if (drawRaf) return;
    const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (fn) => setTimeout(fn, 16);
    drawRaf = raf(() => {
      drawRaf = 0;
      paint(widgetCanvas, widgetWrap);
    });
  }

  function hitTest(canvas, mx, my) {
    if (!canvas) return null;
    const items = hitState.items;
    for (let i = items.length - 1; i >= 0; i--) {
      const h = items[i];
      const dx = mx - h.px;
      const dy = my - h.py;
      if (Math.abs(dx) <= h.half && Math.abs(dy) <= h.half) return h.place;
    }
    return null;
  }

  function pointerDown(e) {
    isPanning = true;
    didDrag = false;
    panStart = { x: e.clientX, y: e.clientY, panX, panY };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function pointerMove(e, canvas) {
    if (isPanning) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didDrag = true;
      panX = panStart.panX + dx;
      panY = panStart.panY + dy;
      scheduleDraw();
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const found = hitTest(canvas, e.clientX - rect.left, e.clientY - rect.top);
    if (found) {
      let text = found.discovered ? (found.name || found.id) : 'Uncharted';
      if (found.areaName && found.discovered) text += ' · ' + found.areaName;
      if (found.current || isCurrentPlace(found.id, currentRoomId)) text += ' (you are here)';
      else if (found.discovered) text += ' · travel';
      tooltip = { visible: true, text, x: e.clientX - rect.left, y: e.clientY - rect.top };
    } else {
      tooltip = { ...tooltip, visible: false };
    }
  }

  function pointerUp(e, canvas) {
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (isPanning && !didDrag) {
      const rect = canvas.getBoundingClientRect();
      const found = hitTest(canvas, e.clientX - rect.left, e.clientY - rect.top);
      if (found) {
        selectedId = found.id;
        if (store && store.selectMapPlace) store.selectMapPlace(found.id);
        scheduleDraw();
      }
    }
    isPanning = false;
    didDrag = false;
  }

  function onWheel(e) {
    e.preventDefault();
    userScale = clampMapScale(userScale * (e.deltaY < 0 ? 1.12 : 0.89));
    scheduleDraw();
  }

  function resolveHerePlace() {
    return groupForRoom(surfaceGroups(visiblePlaces, activeLayer), currentRoomId) || null;
  }

  function applyRecenterToYou(keepScale = true) {
    if (!keepScale) userScale = 1;
    const size = readStageSize(widgetWrap);
    const here = resolveHerePlace();
    if (here && size.w >= 4 && size.h >= 4) {
      const pan = panToCenterPlace(surfaceGroups(visiblePlaces, activeLayer), here, size.w, size.h, userScale, atlas.paths || []);
      panX = pan.panX;
      panY = pan.panY;
    } else {
      panX = 0;
      panY = 0;
    }
  }

  function openOverview() {
    if (store && store.openMapOverview) store.openMapOverview(selectedId);
    else if (store && store.setMapOverviewOpen) store.setMapOverviewOpen(true);
  }

  function inspectSelected() {
    if (store && store.openMapOverview) store.openMapOverview(selectedId || currentRoomId);
  }

  function closeOverview() {
    if (store && store.closeMapOverview) store.closeMapOverview();
    else if (store && store.setMapOverviewOpen) store.setMapOverviewOpen(false);
  }

  function toggleMaximize() {
    if (mapOpen) closeOverview();
    else openOverview();
  }

  function onStageResize(wrap, lastRef, setLast) {
    const next = readStageSize(wrap);
    if (!shouldRepaintSize(lastRef, next)) return;
    setLast(next);
    scheduleDraw();
  }

  let observedWidget = null;
  $: if (widgetWrap !== observedWidget) {
    if (widgetObserver) widgetObserver.disconnect();
    observedWidget = widgetWrap;
    lastWidgetSize = null;
    if (widgetWrap) {
      widgetObserver = new ResizeObserver(() => {
        onStageResize(widgetWrap, lastWidgetSize, (s) => { lastWidgetSize = s; });
      });
      widgetObserver.observe(widgetWrap);
      lastWidgetSize = readStageSize(widgetWrap);
      scheduleDraw();
    }
  }

  onMount(() => {
    stopTileListener = onMapTilesReady(() => scheduleDraw());
  });

  onDestroy(() => {
    if (stopTileListener) stopTileListener();
    if (drawRaf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(drawRaf);
    if (widgetObserver) widgetObserver.disconnect();
    cancelTravel();
    // Tab remounts must NOT close the fullscreen overlay (MapOverviewOverlay owns it).
  });
</script>

<style>
  .atlas {
    width: 100%;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    background: var(--panel-bg, #0d1117);
    border-radius: var(--panel-radius, 8px);
    border: 1px solid var(--panel-border, rgba(255, 255, 255, 0.1));
    overflow: hidden;
  }
  .toolbar {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    color: #cbd5e1;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .toolbar i { font-size: 16px; color: #94a3b8; }
  .spacer { flex: 1; }
  .icon-btn {
    cursor: pointer;
    background: none;
    border: none;
    color: #94a3b8;
    padding: 2px;
    border-radius: 4px;
    display: flex;
    align-items: center;
  }
  .icon-btn:hover { color: #e2e8f0; background: rgba(255,255,255,0.08); }
  .travel { font-size: 10px; color: #22d3ee; text-transform: none; letter-spacing: 0; }
  .stage { flex: 1 1 0; min-height: 0; position: relative; overflow: hidden; }
  canvas { position: absolute; inset: 0; display: block; width: 100%; height: 100%; cursor: grab; touch-action: none; }
  canvas:active { cursor: grabbing; }
  .tooltip {
    position: absolute;
    background: rgba(2, 6, 23, 0.92);
    color: #e2e8f0;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 11px;
    pointer-events: none;
    white-space: nowrap;
    transform: translate(-50%, -110%);
    border: 1px solid rgba(148,163,184,0.25);
  }
  .atlas-compact {
    min-height: 0;
  }
  .stage-compact {
    min-height: 96px;
  }
  .compact-open {
    position: absolute;
    right: 8px;
    bottom: 8px;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: rgba(2, 6, 23, 0.82);
    border: 1px solid rgba(245, 158, 11, 0.45);
    color: #fbbf24;
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 10px;
    font-weight: 600;
    cursor: pointer;
  }
  .compact-open:hover { background: rgba(245, 158, 11, 0.2); }
  .compact-open i { font-size: 14px; }
  .compact-open-live {
    border-color: rgba(34, 211, 238, 0.55);
    color: #67e8f9;
  }
  .intel-strip {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-top: 1px solid rgba(255,255,255,0.08);
    background: rgba(8, 10, 16, 0.9);
    font-size: 10px;
    color: #c9c0b0;
  }
  .intel-strip-name { font-weight: 700; color: #f3ead4; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .intel-strip-meta { color: #8a8070; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .intel-strip-btn {
    border: 1px solid rgba(212,175,55,0.45);
    background: transparent;
    color: #e0b84a;
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    padding: 2px 6px;
    border-radius: 3px;
    cursor: pointer;
    flex: 0 0 auto;
  }
  .intel-strip-btn:disabled {
    opacity: 0.45;
    cursor: default;
  }
  .intel-strip-btn.travel {
    border-color: rgba(34, 211, 238, 0.55);
    background: rgba(34, 211, 238, 0.12);
    color: #67e8f9;
    font-weight: 700;
  }
  .intel-strip-btn.travel:hover:not(:disabled) {
    background: rgba(34, 211, 238, 0.22);
  }
  .intel-strip-btn.ghost {
    border-color: rgba(148, 163, 184, 0.35);
    color: #94a3b8;
  }
  .intel-strip-actions {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex: 0 0 auto;
    margin-left: auto;
  }
  .toolbar-title {
    display: none; /* tab already says Map — avoid double header */
  }
  @media (max-width: 768px) {
    .icon-btn {
      min-width: 40px;
      min-height: 40px;
      justify-content: center;
    }
    .intel-strip {
      padding: 8px;
      gap: 8px;
    }
    .intel-strip-btn {
      min-height: 40px;
      min-width: 72px;
      font-size: 11px;
      padding: 6px 10px;
      touch-action: manipulation;
    }
    .compact-open {
      min-height: 40px;
      padding: 8px 10px;
      font-size: 12px;
      touch-action: manipulation;
    }
  }
</style>

<div class="atlas atlas-compact">
  <div class="toolbar">
    <span class="toolbar-title" aria-hidden="true">Map</span>
    {#if isTraveling}<span class="travel">Traveling…</span>
    {:else if mapOpen}<span class="travel">Fullscreen</span>
    {:else}<span class="travel" style="color:#94a3b8">Tap a room to travel</span>{/if}
    <span class="spacer"></span>
    <button class="icon-btn" title={mapOpen ? 'Close fullscreen map' : 'Open fullscreen map'} on:click={toggleMaximize} aria-label={mapOpen ? 'Close fullscreen map' : 'Open fullscreen map'}>
      <i class="material-icons">{mapOpen ? 'close_fullscreen' : 'open_in_full'}</i>
    </button>
  </div>
  <div class="stage stage-compact" bind:this={widgetWrap}>
    <canvas
      bind:this={widgetCanvas}
      on:pointerdown={pointerDown}
      on:pointermove={(e) => pointerMove(e, widgetCanvas)}
      on:pointerup={(e) => pointerUp(e, widgetCanvas)}
      on:pointerleave={() => tooltip = { ...tooltip, visible: false }}
      on:wheel={onWheel}
      on:dblclick={toggleMaximize}
    ></canvas>
    {#if tooltip.visible}
      <div class="tooltip" style="left: {tooltip.x}px; top: {tooltip.y}px;">{tooltip.text}</div>
    {/if}
  </div>
  {#if selectedPlace}
    <div class="intel-strip">
      <span class="intel-strip-name">{selectedPlace.discovered ? (selectedPlace.name || selectedPlace.id) : 'Uncharted'}</span>
      <span class="intel-strip-meta">{selectedPlace.discovered ? (selectedPlace.areaName || selectedPlace.biome || '') : 'Fog'}</span>
      <div class="intel-strip-actions">
        <button
          type="button"
          class="intel-strip-btn travel"
          on:click={requestTravel}
          disabled={!canTravel || isTraveling}
        >{isTraveling ? 'Traveling…' : (canTravel ? 'Travel' : (selectedPlace.discovered ? 'Here' : 'Fog'))}</button>
        <button type="button" class="intel-strip-btn ghost" on:click={inspectSelected} title="Open room details">Inspect</button>
      </div>
    </div>
  {/if}
</div>
